import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Table, Tag, Space, message, Typography, Divider } from 'antd';
import { MailOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { UserFrom1C, UserRole } from '../../types/admin.types';
import adminApi from '../../services/adminApi';

const { Title, Text } = Typography;
const { Option } = Select;

interface InviteUsersModalProps {
    visible: boolean;
    users: UserFrom1C[];
    onCancel: () => void;
    onSuccess: () => void;
}

interface InviteFormData {
    email: string;
    role: UserRole;
    fullName: string;
}

export const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
    visible,
    users,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [inviteList, setInviteList] = useState<InviteFormData[]>([]);
    const [step, setStep] = useState<'select' | 'confirm'>('select');

    useEffect(() => {
        console.log('InviteUsersModal useEffect:', { visible, users });
        if (visible && users.length > 0) {
            // Автоматически заполняем форму данными из выбранных пользователей
            const initialData = users.map(user => ({
                email: user.email,
                role: user.role as UserRole,
                fullName: user.fullName
            }));
            console.log('Setting initial data:', initialData);
            setInviteList(initialData);
            setStep('confirm');
        } else if (visible && users.length === 0) {
            console.log('Setting step to select');
            setStep('select');
            setInviteList([]);
        }
    }, [visible, users]);

    const handleAddUser = () => {
        const values = form.getFieldsValue();
        console.log('Form values:', values); // Добавляем логирование

        if (!values.email || !values.role || !values.fullName) {
            message.error('Заполните все поля');
            return;
        }

        // Проверяем, не добавлен ли уже пользователь с таким email
        if (inviteList.some(user => user.email === values.email)) {
            message.error('Пользователь с таким email уже добавлен');
            return;
        }

        console.log('Adding user to invite list:', values); // Добавляем логирование
        const newInviteList = [...inviteList, values];
        setInviteList(newInviteList);
        form.resetFields();

        // Переключаемся на этап подтверждения после добавления пользователя
        setStep('confirm');
    };

    const handleRemoveUser = (email: string) => {
        setInviteList(inviteList.filter(user => user.email !== email));
    };

    const handleSendInvites = async () => {
        if (inviteList.length === 0) {
            message.error('Добавьте хотя бы одного пользователя');
            return;
        }

        console.log('Sending invites for users:', inviteList); // Добавляем логирование
        setLoading(true);
        try {
            const promises = inviteList.map(user => {
                console.log('Sending invite for user:', user); // Добавляем логирование
                return adminApi.inviteUser({
                    userId: user.email, // Используем email как ID для простоты
                    fullName: user.fullName,
                    email: user.email,
                    role: user.role
                });
            });

            const results = await Promise.allSettled(promises);
            console.log('Invite results:', results); // Добавляем логирование

            let successCount = 0;
            let errorCount = 0;

            results.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value.data.success) {
                    successCount++;
                } else {
                    errorCount++;
                    console.error(`Ошибка приглашения для ${inviteList[index].email}:`, result);
                }
            });

            if (successCount > 0) {
                message.success(`Приглашения отправлены для ${successCount} пользователей`);
            }
            if (errorCount > 0) {
                message.error(`Ошибка отправки для ${errorCount} пользователей`);
            }

            if (successCount > 0) {
                onSuccess();
            }
        } catch (error) {
            console.error('Ошибка отправки приглашений:', error);
            message.error('Ошибка отправки приглашений');
        } finally {
            setLoading(false);
        }
    };

    const getRoleColor = (role: UserRole) => {
        switch (role) {
            case UserRole.MODERATOR: return 'blue';
            case UserRole.SIGNATORY: return 'green';
            default: return 'default';
        }
    };

    const getRoleLabel = (role: UserRole) => {
        switch (role) {
            case UserRole.MODERATOR: return 'Модератор';
            case UserRole.SIGNATORY: return 'Подписант';
            default: return role;
        }
    };

    const inviteColumns = [
        {
            title: 'ФИО',
            dataIndex: 'fullName',
            key: 'fullName',
            render: (text: string) => (
                <Space>
                    <UserOutlined />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text: string) => (
                <Space>
                    <MailOutlined />
                    {text}
                </Space>
            )
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role: UserRole) => (
                <Tag color={getRoleColor(role)}>
                    {getRoleLabel(role)}
                </Tag>
            )
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: InviteFormData) => (
                <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => handleRemoveUser(record.email)}
                >
                    Удалить
                </Button>
            )
        }
    ];

    const handleModalCancel = () => {
        setInviteList([]);
        setStep('select');
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="Приглашение пользователей"
            open={visible}
            onCancel={handleModalCancel}
            width={800}
            footer={null}
        >
            {step === 'select' && (
                <div>
                    <Title level={4}>Добавить пользователя</Title>
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="fullName"
                            label="ФИО"
                            rules={[{ required: true, message: 'Введите ФИО' }]}
                        >
                            <Input placeholder="Введите полное имя" />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Введите email' },
                                { type: 'email', message: 'Введите корректный email' }
                            ]}
                        >
                            <Input placeholder="example@company.com" />
                        </Form.Item>

                        <Form.Item
                            name="role"
                            label="Роль"
                            rules={[{ required: true, message: 'Выберите роль' }]}
                        >
                            <Select placeholder="Выберите роль">
                                <Option value={UserRole.MODERATOR}>Модератор</Option>
                                <Option value={UserRole.SIGNATORY}>Подписант</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                onClick={handleAddUser}
                                onMouseDown={() => console.log('Button clicked')}
                            >
                                Добавить в список
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            )}

            {step === 'confirm' && (
                <div>
                    <Title level={4}>Подтверждение приглашений</Title>
                    <Text type="secondary" className="block mb-4">
                        Следующим пользователям будут отправлены приглашения на email:
                    </Text>

                    <Table
                        columns={inviteColumns}
                        dataSource={inviteList}
                        rowKey="email"
                        pagination={false}
                        size="small"
                    />

                    <Divider />

                    <div className="flex justify-between">
                        <Button onClick={() => setStep('select')}>
                            Добавить еще
                        </Button>
                        <div className="space-x-2">
                            <Button onClick={handleModalCancel}>
                                Отмена
                            </Button>
                            <Button
                                type="primary"
                                loading={loading}
                                onClick={handleSendInvites}
                                icon={<CheckCircleOutlined />}
                            >
                                Отправить приглашения
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};
